import React, { useRef, useState } from "react";
import { Share2, Download, Link2, Check, Star, Award, Lightbulb } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import Panda from "./Panda";

const STEP_LABELS = { learn: "Learn", talk: "Talk", practice: "Practice", story: "Story", dodont: "Do & Don't", quiz: "Quiz" };
const STEP_KEYS = Object.keys(STEP_LABELS);

const dataUrlToFile = async (url, name) => new File([await (await fetch(url)).blob()], name, { type: "image/png" });

// One-screen recap of a lesson for a child, shareable via Web Share / copy link / PNG download.
const RecapCard = ({ open, onOpenChange, module: m, child, autoOpened = false }) => {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  if (!m || !child) return null;
  const done = child.completed?.[m.id] || {};
  const stepsDone = STEP_KEYS.filter((k) => done[k] !== undefined && done[k] !== false);
  const quiz = typeof done.quiz === "number" ? done.quiz : null;
  const certified = !!done.certifiedAt;
  const bigIdea = m.objectives?.[0] || m.tagline;
  const link = `${window.location.origin}/lessons/${m.slug}`;
  const shareText = `${child.panda.name} finished ${stepsDone.length} of 6 steps of Lesson ${m.num}: ${m.title}${quiz !== null ? ` (quiz ${quiz}/5)` : ""}${certified ? " - certificate earned!" : ""} on Teach Your Child Credit. Big idea: ${bigIdea}`;

  const makePng = () => toPng(cardRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff", skipFonts: true });

  const share = async () => {
    setBusy(true);
    try {
      const data = { title: `${child.panda.name}'s Lesson ${m.num} recap`, text: shareText, url: link };
      if (navigator.share) {
        try {
          const file = await dataUrlToFile(await makePng(), `${child.panda.name}-lesson-${m.num}-recap.png`);
          if (navigator.canShare?.({ files: [file] })) { await navigator.share({ ...data, files: [file] }); return; }
        } catch { /* fall through to text share */ }
        await navigator.share(data);
      } else {
        await navigator.clipboard.writeText(`${shareText}\n${link}`);
        toast.success("Recap copied - paste it into WhatsApp or a text");
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not share", { description: e?.message });
    } finally { setBusy(false); }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(`${shareText}\n${link}`); toast.success("Copied to clipboard"); } catch { window.prompt("Copy this:", `${shareText}\n${link}`); }
  };

  const download = async () => {
    setBusy(true);
    try {
      const a = document.createElement("a");
      a.href = await makePng();
      a.download = `${child.panda.name}-lesson-${m.num}-recap.png`;
      a.click();
      toast.success("Recap image downloaded");
    } catch (e) { toast.error("Could not create the image", { description: e?.message }); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 overflow-hidden rounded-3xl border-0 bg-[#f7fdfe] [&>button]:bg-white/80 [&>button]:rounded-full [&>button]:p-1 [&>button]:opacity-100" data-testid="recap-dialog">
        <DialogTitle className="sr-only">Lesson recap</DialogTitle>
        <div ref={cardRef} className="bg-white" data-testid="recap-card">
          <div className="relative px-6 pt-6 pb-8" style={{ background: m.color }}>
            <p className="text-xs font-bold uppercase tracking-widest" style={{ color: m.dark }}>{autoOpened ? "Lesson complete!" : "Lesson recap"} · Lesson {m.num}</p>
            <div className="mt-2 flex items-center gap-4">
              <Panda size={96} accessory={child.panda.accessory} scarf={child.panda.scarf} item={m.pandaItem} />
              <div className="min-w-0">
                <p className="text-white font-bold text-2xl leading-tight drop-shadow-[0_2px_0_rgba(0,0,0,0.2)]" style={{ fontFamily: "Literata, serif" }} data-testid="recap-child">{child.panda.name}</p>
                <p className="font-semibold leading-snug" style={{ color: m.dark }} data-testid="recap-title">{m.title}</p>
              </div>
            </div>
          </div>
          <div className="px-6 py-5">
            <div className="flex flex-wrap gap-1.5" data-testid="recap-steps">
              {STEP_KEYS.map((k) => {
                const ok = stepsDone.includes(k);
                return <span key={k} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${ok ? "bg-[#63c79a] text-white" : "bg-[#e9f9fd] text-[#0c4a6e]/60"}`}>{ok && <Check size={12} />} {STEP_LABELS[k]}</span>;
              })}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-2xl bg-[#f7fdfe] p-3"><p className="text-2xl font-bold text-[#0c4a6e]" data-testid="recap-steps-count">{stepsDone.length}<span className="text-sm text-gray-400">/6</span></p><p className="text-[11px] text-gray-500 font-bold uppercase">steps</p></div>
              <div className="rounded-2xl bg-[#f7fdfe] p-3"><p className="text-2xl font-bold text-[#0c4a6e]" data-testid="recap-quiz">{quiz !== null ? <>{quiz}<span className="text-sm text-gray-400">/5</span></> : "-"}</p><p className="text-[11px] text-gray-500 font-bold uppercase">quiz</p></div>
              <div className="rounded-2xl bg-[#f7fdfe] p-3 flex flex-col items-center justify-center">{certified ? <Award size={26} className="text-[#f3c932]" fill="#f3c932" /> : <Star size={26} className="text-gray-300" />}<p className="text-[11px] text-gray-500 font-bold uppercase mt-1">{certified ? "certified" : "in progress"}</p></div>
            </div>
            <div className="mt-4 rounded-2xl bg-[#fff8e1] border border-[#f3c932] p-4 flex gap-3">
              <Lightbulb size={20} className="text-[#f7931e] flex-shrink-0 mt-0.5" />
              <div><p className="text-[11px] font-bold uppercase tracking-wider text-[#8a6d00]">Big idea</p><p className="text-sm text-[#5a4600] font-medium leading-snug" data-testid="recap-big-idea">{bigIdea}</p></div>
            </div>
            <p className="mt-4 text-[10px] text-gray-400 text-center">teachyourchildcredit.com · Based on the book by Ken Bossard, MBA</p>
          </div>
        </div>
        <div className="px-6 py-4 flex flex-wrap gap-2 justify-center bg-[#f7fdfe]">
          <button onClick={share} disabled={busy} className="btn-pill bg-[#25D366] hover:bg-[#1fb757] text-white inline-flex items-center gap-2 disabled:opacity-60" data-testid="recap-share"><Share2 size={16} /> Share</button>
          <button onClick={copy} className="btn-pill bg-white border-2 border-[#c9ecf5] text-[#0c4a6e] hover:bg-[#e9f9fd] inline-flex items-center gap-2" data-testid="recap-copy"><Link2 size={16} /> Copy</button>
          <button onClick={download} disabled={busy} className="btn-pill bg-white border-2 border-[#c9ecf5] text-[#0c4a6e] hover:bg-[#e9f9fd] inline-flex items-center gap-2 disabled:opacity-60" data-testid="recap-download"><Download size={16} /> Image</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RecapCard;
